import { forwardRef, useState } from 'react';
import { Alert } from '@shared/components';
import { isEmbeddableCheckoutUrl } from '../services/checkoutUrl';

/** The frame region has a visible skeleton until `onLoad` fires, so a slow
 *  third-party load reads as "loading", not as an apparently-empty box
 *  (accessibility spec point 9). */
function CheckoutFrameSkeleton() {
    return (
        <div
            aria-hidden="true"
            className="absolute inset-0 flex animate-pulse items-center justify-center bg-mp-background"
        >
            <span className="text-sm text-mp-muted">Loading payment checkout…</span>
        </div>
    );
}

/** Rendered instead of an iframe when the checkout_url fails the client-side
 *  allow-list (F-D6, S2). Deliberately does not repeat the URL: an untrusted
 *  host is not something a salesperson can act on, and echoing it back would
 *  put attacker-controlled text on screen. */
function UntrustedCheckoutNotice() {
    return (
        <Alert variant="error">
            This payment link could not be verified and has not been opened. Please try again, and
            contact support if this keeps happening.
        </Alert>
    );
}

interface Props {
    checkoutUrl: string;
}

/** Registers NO `message` listener: the forwarded ref exists so the modal's
 *  gated listener can pin one to this frame's `contentWindow`. Pinned by test. */
export const ElemisCheckoutFrame = forwardRef<HTMLIFrameElement, Props>(function ElemisCheckoutFrame(
    { checkoutUrl },
    ref,
) {
    // Hook BEFORE the guard below — a conditional return above a hook
    // violates the rules of hooks and eslint-plugin-react-hooks fails the
    // build.
    const [loaded, setLoaded] = useState(false);

    if (!isEmbeddableCheckoutUrl(checkoutUrl)) {
        return <UntrustedCheckoutNotice />;
    }

    return (
        <div className="relative h-full w-full">
            {!loaded && <CheckoutFrameSkeleton />}
            {/* NO `sandbox`, deliberately: the framed flow redirects to Acquired.com
                and creditsafeuk.com, and a guessed token set breaks 3DS step-up. */}
            <iframe
                // `key` on the URL: `loaded` is component state and is NOT
                // reset when `checkoutUrl` changes, so without this a second
                // URL would render with the skeleton already dismissed.
                // Inert today (the URL only ever goes null -> value in this
                // plan) and load-bearing the moment a supersede control
                // lands (G3b).
                key={checkoutUrl}
                ref={ref}
                src={checkoutUrl}
                title="Complete your payment with MotorPlan Payments"
                onLoad={() => setLoaded(true)}
                className="block h-full w-full border-0"
                referrerPolicy="strict-origin"
                allow="payment"
            />
        </div>
    );
});
